import React, { useState } from 'react';
import { useCustomTemplates } from './hooks/useCustomTemplates';
import { ref, remove } from 'firebase/database';
import { database as db, auth } from '../firebaseConfig';
import './MyTemplates.css';

const WIDGET_TYPE_CATEGORIES = [
    'All', 'Header', 'Footer', 'Services', 'Q&A', 'Testimonials', 
    'Content', 'Media', 'Product', 'Contact', 'Other'
];

function MyTemplates() {
    const { customTemplates, loading, error, getTemplatesByCategory, getTemplateStats } = useCustomTemplates();
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [copySuccess, setCopySuccess] = useState(null);

    // Filter templates by category
    const filteredTemplates = selectedCategory === 'All' 
        ? customTemplates 
        : getTemplatesByCategory(selectedCategory);

    // Delete template
    const handleDeleteTemplate = async (templateId) => {
        try {
            const user = auth.currentUser;
            if (!user) return;

            const templateRef = ref(db, `customWidgetTemplates/${user.uid}/${templateId}`);
            await remove(templateRef);
            
            console.log('✅ Template deleted:', templateId);
            setDeleteConfirm(null);
        } catch (err) {
            console.error('❌ Error deleting template:', err);
            alert('Failed to delete template: ' + err.message);
        }
    };

    // Copy template data to clipboard (for debugging/sharing)
    const handleCopyTemplateData = (template) => {
        const templateData = JSON.stringify(template.widgetData, null, 2);
        navigator.clipboard.writeText(templateData);
        setCopySuccess(template.templateId);
        setTimeout(() => setCopySuccess(null), 2000);
    };

    // Use template - will be called from parent component
    const handleUseTemplate = (template) => {
        // This will be implemented when integrating with page editor
        console.log('Using template:', template.templateName);
        alert(`Template "${template.templateName}" would be added to your page. This will be implemented when integrated with the page editor.`);
    };

    const stats = getTemplateStats();

    if (loading) {
        return (
            <div className="my-templates-loading">
                <div className="loading-spinner"></div>
                <p>Loading your templates...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="my-templates-error">
                <h2>⚠️ Error Loading Templates</h2>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="my-templates-container">
            {/* Header */}
            <div className="my-templates-header">
                <div className="header-content">
                    <h1>
                        <i className="fas fa-star"></i>
                        My Custom Templates
                    </h1>
                    <p>Manage and reuse your saved widget configurations</p>
                </div>
                
                {/* Stats */}
                <div className="templates-stats">
                    <div className="stat-card">
                        <span className="stat-number">{stats.total}</span>
                        <span className="stat-label">Total Templates</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-number">{Object.keys(stats.byCategory).length}</span>
                        <span className="stat-label">Categories</span>
                    </div>
                </div>
            </div>

            {/* Category Filter */}
            <div className="templates-filter">
                <div className="filter-buttons">
                    {WIDGET_TYPE_CATEGORIES.map(category => (
                        <button
                            key={category}
                            className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                            onClick={() => setSelectedCategory(category)}
                        >
                            {category}
                            {category !== 'All' && stats.byCategory[category] && (
                                <span className="category-count">{stats.byCategory[category]}</span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Templates Grid */}
            {filteredTemplates.length > 0 ? (
                <div className="templates-grid">
                    {filteredTemplates.map(template => (
                        <div key={template.templateId} className="template-card">
                            {/* Thumbnail */}
                            <div className="template-thumbnail">
                                <img 
                                    src={template.thumbnailImage} 
                                    alt={template.templateName}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                                <div className="template-thumbnail-fallback" style={{ display: 'none' }}>
                                    <i className="fas fa-puzzle-piece"></i>
                                    <span>No Preview</span>
                                </div>
                                
                                {/* Category Badge */}
                                <div className="template-category-badge">
                                    {template.widgetType}
                                </div>
                            </div>

                            {/* Content */}
                            <div className="template-content">
                                <h3 className="template-name">{template.templateName}</h3>
                                <p className="template-type">
                                    <i className="fas fa-cogs"></i>
                                    {template.originalWidgetType}
                                </p>
                                <p className="template-description">{template.aiStyleInfo}</p>
                                
                                {/* Meta Info */}
                                <div className="template-meta">
                                    <span className="template-date">
                                        <i className="fas fa-calendar"></i>
                                        {new Date(template.createdAt).toLocaleDateString()}
                                    </span>
                                    <span className="template-usage">
                                        <i className="fas fa-chart-bar"></i>
                                        Used {template.usageCount || 0} times
                                    </span>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="template-actions">
                                <button 
                                    className="action-btn use-btn"
                                    onClick={() => handleUseTemplate(template)}
                                    title="Add this template to your page"
                                >
                                    <i className="fas fa-plus"></i>
                                    Use Template
                                </button>
                                
                                <div className="action-menu">
                                    <button 
                                        className="action-btn copy-btn"
                                        onClick={() => handleCopyTemplateData(template)}
                                        title="Copy template data"
                                    >
                                        <i className="fas fa-copy"></i>
                                        {copySuccess === template.templateId ? 'Copied!' : 'Copy Data'}
                                    </button>
                                    
                                    <button 
                                        className="action-btn delete-btn"
                                        onClick={() => setDeleteConfirm(template.templateId)}
                                        title="Delete template"
                                    >
                                        <i className="fas fa-trash"></i>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="no-templates">
                    <div className="no-templates-content">
                        <i className="fas fa-star-o"></i>
                        <h2>No Templates Found</h2>
                        <p>
                            {selectedCategory === 'All' 
                                ? "You haven't created any custom templates yet. Start by editing a widget and clicking 'Save as Template'."
                                : `No templates found in the "${selectedCategory}" category.`
                            }
                        </p>
                        <button 
                            className="create-template-btn"
                            onClick={() => window.history.back()}
                        >
                            <i className="fas fa-arrow-left"></i>
                            Go Back to Page Editor
                        </button>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="delete-modal-overlay">
                    <div className="delete-modal">
                        <h3>🗑️ Delete Template</h3>
                        <p>Are you sure you want to delete this template? This action cannot be undone.</p>
                        <div className="delete-modal-actions">
                            <button 
                                className="cancel-btn"
                                onClick={() => setDeleteConfirm(null)}
                            >
                                Cancel
                            </button>
                            <button 
                                className="confirm-delete-btn"
                                onClick={() => handleDeleteTemplate(deleteConfirm)}
                            >
                                Delete Template
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MyTemplates;
